import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Upload, Loader2, Camera } from "lucide-react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { toast } from "sonner";

export default function VisualSearch() {
  const { visualOpen, setVisualOpen } = useApp();
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef();

  const reset = () => {
    setPreview(null);
    setScanning(false);
    setResult(null);
  };

  const close = () => {
    setVisualOpen(false);
    setTimeout(reset, 300);
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUri = reader.result;
      setPreview(dataUri);
      setScanning(true);
      setResult(null);
      try {
        const { data } = await api.post("/search/visual", { image_base64: dataUri });
        setResult(data);
      } catch (e) {
        toast.error("Visual search failed. Try another image.");
      } finally {
        setScanning(false);
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <AnimatePresence>
      {visualOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-md flex items-center justify-center p-4"
          data-testid="visual-search-modal"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="w-full max-w-4xl bg-[#FDFBF7] max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-6 border-b border-black/10">
              <div>
                <div className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#FF3B30]">Nova.vision</div>
                <h2 className="font-display font-black text-2xl leading-none tracking-tighter mt-1">Visual search</h2>
              </div>
              <button onClick={close} data-testid="visual-close-btn" className="hover:text-[#FF3B30]">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {!preview ? (
                <div
                  onClick={() => inputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
                  className="border-2 border-dashed border-black/20 hover:border-[#FF3B30] p-16 text-center cursor-pointer transition-colors group"
                  data-testid="visual-drop-zone"
                >
                  <Camera className="w-10 h-10 mx-auto mb-4 text-neutral-400 group-hover:text-[#FF3B30] transition-colors" />
                  <p className="font-display text-xl tracking-tight">Drop a product image or click to upload</p>
                  <p className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-2">
                    Nova will decode & find matches from our catalog
                  </p>
                  <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files[0])}
                    data-testid="visual-file-input"
                  />
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="relative aspect-square overflow-hidden bg-neutral-100">
                    <img src={preview} alt="Query" className="w-full h-full object-cover" />
                    {scanning && <div className="scan-line animate-scan" />}
                    {scanning && (
                      <div className="absolute inset-0 bg-black/10 flex items-end p-4">
                        <div className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-white bg-black/60 px-2 py-1">
                          Nova.decoding…
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    {scanning && (
                      <div className="flex items-center gap-2 text-sm text-neutral-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="font-mono-brand text-xs uppercase tracking-widest">Analyzing image</span>
                      </div>
                    )}
                    {result && (
                      <div>
                        <div className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#FF3B30]">Nova sees</div>
                        <p className="font-display text-lg leading-tight mt-1 mb-4">{result.description || "Similar catalog items"}</p>
                        {result.products.length === 0 ? (
                          <p className="text-sm text-neutral-500">No matches found. Try another angle.</p>
                        ) : (
                          <div className="grid grid-cols-2 gap-3">
                            {result.products.map((p) => (
                              <Link key={p.id} to={`/product/${p.id}`} onClick={close} className="group">
                                <div className="aspect-square overflow-hidden bg-neutral-100">
                                  <img src={p.image + "?auto=format&fit=crop&w=400&q=80"} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                </div>
                                <div className="font-display text-sm mt-1 leading-tight group-hover:text-[#FF3B30]">{p.name}</div>
                                <div className="font-mono-brand text-[10px] text-neutral-500">${p.price}</div>
                              </Link>
                            ))}
                          </div>
                        )}
                        <button
                          onClick={reset}
                          className="mt-6 font-mono-brand text-xs uppercase tracking-[0.2em] border-b border-black hover:border-[#FF3B30] hover:text-[#FF3B30] pb-0.5"
                          data-testid="visual-try-again"
                        >
                          Try another →
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
